// ===================================================
// parseQuestions.js — GPT 응답 텍스트 파싱 + 후처리 유틸
//
// GPT가 돌려주는 텍스트를 우리가 쓸 수 있는
// 배열 형태로 변환합니다.
//
// 전략 태그([factual] 등)가 포함된 질문에서
// 태그를 분리하고 깨끗한 질문만 반환합니다.
//
// 후처리 기능:
//   - 질문 품질 검증 (금지 패턴, 길이, 개수)
//   - 반복 질문 감지 (Jaccard 유사도)
//   - 키워드 정제 (띄어쓰기 포함 제거, 중복 제거)
// ===================================================

// 유효한 전략 태그 목록
const VALID_STRATEGIES = ['factual', 'hypothetical', 'comparative', 'relational', 'paradoxical', 'condensing'];

// 질문 문장을 정제합니다.
// GPT가 따옴표, 서두 텍스트, 대시, Q번호 등을 붙이는 경우의 안전망입니다.
function cleanQuestion(q) {
  return q
    .replace(/^["'「」『』]+|["'「」『』]+$/g, '')              // 따옴표 제거
    .replace(/^(네,?\s*|물론,?\s*|좋은 질문이에요,?\s*|알겠습니다,?\s*|물론이죠,?\s*)/, '')  // GPT 서두 제거
    .replace(/^Q\d+[:.]\s*/i, '')                              // "Q1:" 접두사 제거
    .replace(/\s*[—–-]\s*/g, ' ')                              // 대시 앞뒤 공백 포함해 공백 하나로 대체
    .replace(/\s+/g, ' ')                                       // 연속 공백 정리
    .trim()
    .replace(/[^?]$/, m => m + '?');                            // 물음표 누락 시 추가
}

// 질문 텍스트에서 전략 태그를 추출하고 분리합니다.
// 입력: "[factual] 팀장이 뭐라고 했을 때 제일 억울했어요?"
// 반환: { question: "팀장이 뭐라고 했을 때 제일 억울했어요?", strategy: "factual" }
function extractStrategy(text) {
  const match = text.match(/^\[(\w+)\]\s*(.+)/);
  if (match && VALID_STRATEGIES.includes(match[1])) {
    return { question: cleanQuestion(match[2]), strategy: match[1] };
  }
  return { question: cleanQuestion(text), strategy: null };
}

// GPT 응답에서 "1. 질문", "2. 질문" 형태의 줄을 찾아 배열로 반환
// 최대 2개까지만 사용합니다.
// 전략 태그는 제거하고 깨끗한 질문만 반환합니다.
export function parseQuestions(gptText) {
  const lines = gptText.split('\n').filter(l => l.trim());
  const questions = [];
  for (const line of lines) {
    const match = line.match(/^\d+[.)]\s*(.+)/);
    if (match) {
      const { question } = extractStrategy(match[1]);
      questions.push(question);
    }
  }
  // 폴백: 번호가 없는 경우 물음표로 끝나는 줄을 질문으로 인식
  if (questions.length === 0) {
    for (const line of lines) {
      const cleaned = cleanQuestion(line.trim());
      if (cleaned.length > 5 && cleaned.endsWith('?')) {
        questions.push(cleaned);
      }
    }
  }
  return questions.slice(0, 2);
}

// GPT 응답에서 질문과 전략 태그를 함께 추출합니다.
// 반환: [{ question: "...", strategy: "factual" }, ...]
// 전략 태그 데이터 수집용으로 사용됩니다.
export function parseQuestionsWithStrategies(gptText) {
  const lines = gptText.split('\n').filter(l => l.trim());
  const results = [];
  for (const line of lines) {
    const match = line.match(/^\d+[.)]\s*(.+)/);
    if (match) {
      results.push(extractStrategy(match[1]));
    }
  }
  return results.slice(0, 2);
}

// GPT 응답에서 쉼표로 구분된 키워드 목록을 배열로 변환
// 띄어쓰기 포함 키워드 제거, 중복 제거, 최대 10개
export function parseKeywords(gptText) {
  return gptText
    .split(',')
    .map(k => k.trim().replace(/^\d+\.\s*/, ''))      // "1. " 접두사 제거
    .filter(k => k && !k.includes(' ') && k.length <= 20)  // 띄어쓰기 포함 시 제거
    .filter((k, i, arr) => arr.indexOf(k) === i)       // 중복 제거
    .slice(0, 10);
}

// 의사결정 모드 전용: GPT가 JSON 형식으로 반환한 응답을 파싱합니다.
// GPT 응답 예시: {"questions":["Q1?","Q2?"],"axes":["decision_criteria","fear"],"strategies":["factual","hypothetical"]}
// 파싱 실패 시 빈 questions/axes/strategies로 안전하게 반환합니다.
export function parseDecisionResponse(gptText) {
  try {
    // GPT가 JSON 앞뒤에 불필요한 텍스트를 붙일 수 있으므로 중괄호 블록만 추출
    const jsonMatch = gptText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return { questions: [], axes: [], strategies: [] };

    const parsed = JSON.parse(jsonMatch[0]);
    const questions = Array.isArray(parsed.questions) ? parsed.questions.slice(0, 2).map(cleanQuestion) : [];
    const axes = Array.isArray(parsed.axes) ? parsed.axes.slice(0, 2) : [];
    const strategies = Array.isArray(parsed.strategies)
      ? parsed.strategies.slice(0, 2).filter(s => VALID_STRATEGIES.includes(s))
      : [];
    return { questions, axes, strategies };
  } catch {
    // JSON 파싱 실패 시 기존 텍스트 파싱으로 폴백
    const parsed = parseQuestionsWithStrategies(gptText);
    return {
      questions: parsed.map(p => p.question),
      axes: [],
      strategies: parsed.map(p => p.strategy).filter(Boolean),
    };
  }
}

// 의사결정 모드 전용: 메타데이터 JSON 파싱
// GPT 응답 예시: {"tentativeDecision":"이직하기로","nextActionHint":"연봉 조건 확인"}
export function parseDecisionMeta(gptText) {
  try {
    const jsonMatch = gptText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return { tentativeDecision: null, nextActionHint: null };

    const parsed = JSON.parse(jsonMatch[0]);
    return {
      tentativeDecision: parsed.tentativeDecision || null,
      nextActionHint: parsed.nextActionHint || null,
    };
  } catch {
    return { tentativeDecision: null, nextActionHint: null };
  }
}


// ── 후처리 4: 질문 품질 검증 (클라이언트용) ──────────────────────
// 스트리밍 완료 후 금지 패턴/길이/개수를 검증하여 로깅합니다.
// 서버의 validateQuestions()와 동일한 로직입니다.

const FORBIDDEN_PATTERNS = [
  '감정은 어땠나요', '기분이 어땠나요', '느낌이 어땠나요',
  '어떤 의미였나요', '어떤 경험이었나요',
  '어떻게 생각하나요', '어떻게 느꼈나요',
  '어떤 생각이 들었나요',
  '어떤 것이었나요', '이야기해줄 수 있나요',
];

export function validateQuestionsClient(questions, expectedCount = 2) {
  const violations = [];
  if (questions.length !== expectedCount) {
    violations.push(`count:${questions.length}/${expectedCount}`);
  }
  for (const q of questions) {
    if (q.length > 80) violations.push(`length:${q.length}`);
    for (const p of FORBIDDEN_PATTERNS) {
      if (q.includes(p)) { violations.push(`forbidden:${p}`); break; }
    }
  }
  return { valid: violations.length === 0, violations };
}


// ── 후처리 6: 반복 질문 감지 ─────────────────────────────────────
// 새 질문이 이전 질문과 너무 비슷한지 단어 기반 Jaccard 유사도로 판별합니다.
// threshold(기본 0.6) 이상이면 반복으로 간주합니다.

function wordSet(text) {
  return new Set(text.replace(/[?.,!]/g, '').split(/\s+/).filter(w => w.length > 1));
}

function jaccardSimilarity(a, b) {
  const setA = wordSet(a);
  const setB = wordSet(b);
  if (!setA.size || !setB.size) return 0;
  let inter = 0;
  for (const w of setA) { if (setB.has(w)) inter++; }
  return inter / (setA.size + setB.size - inter);
}

export function detectRepetition(newQuestions, previousQuestions, threshold = 0.6) {
  const repeated = [];
  for (const nq of newQuestions) {
    for (const pq of previousQuestions) {
      if (jaccardSimilarity(nq, pq) >= threshold) {
        repeated.push({ newQuestion: nq, similarTo: pq });
        break;
      }
    }
  }
  return repeated;
}
